import type { Action, PayloadAction } from '@reduxjs/toolkit';
import type { DisplayParams, ListRequestParams } from '@ducks/common/types';

export type ReportState = {
  /** リフレッシュ */
  refresh: Record<string, never>;
  /** Load中か */
  isLoading: boolean;
  /** 月報フォーマットリストがLoad中か */
  isReportFormatListLoading: boolean;
  /** 送信処理中か */
  isSubmitting: boolean;
  /** 建物リスト */
  propertyList: Property[];
  /** 建物リストのページャ情報 */
  propertyListDisplayParams: DisplayParams;
  /** 契約先リスト */
  contractorList: Contractor[];
  /** 契約先リストのページャ情報 */
  contractorListDisplayParams: DisplayParams;
  /** 月報出力対象リストの建物/契約先名 */
  reportTargetName: string;
  /** 月報出力対象リスト */
  reportItemList: ReportItem[];
  /** 全月報出力対象リスト */
  reportItemListAll: ReportItem[];
  /** 月報出力対象リストのページャ情報 */
  reportItemDisplayParams: DisplayParams;
  /** 月報出力対象リスト */
  reportFormatList: ReportFormat[];
  /** 月報出力対象リストのページャ情報 */
  reportFormatDisplayParams: DisplayParams;
  /** 選択中の月報フォーマット */
  selectedReportFormat: ReportFormat | null;
  /** 選択された月報出力対象リスト */
  selectReportItemList: SelectReportItem[];
};

export enum ReportActionTypes {
  INIT = 'report/init',
  REFRESH = 'report/refresh',
  LOADING = 'report/set/isLoading',
  REPORT_FORMAT_LIST_LOADING = 'report/set/isReportFormatListLoading',
  SUBMITTING = 'report/set/isSubmitting',
  SET_PROPERTY_LIST = 'report/set/propertyList',
  SET_PROPERTY_LIST_DISPLAY_PARAMS = 'report/set/propertyListDisplayParams',
  SET_CONTRACTOR_LIST = 'report/set/contractorList',
  SET_CONTRACTOR_LIST_DISPLAY_PARAMS = 'report/set/contractorListDisplayParams',
  SET_REPORT_TARGET_NAME = 'report/set/reportTargetName',
  SET_REPORT_ITEM_LIST = 'report/set/reportItemList',
  SET_REPORT_ITEM_LIST_ALL = 'report/set/reportItemListAll',
  SET_REPORT_ITEM_DISPLAY_PARAMS = 'report/set/reportItemDisplayParams',
  SET_REPORT_FORMAT_LIST = 'report/set/reportFormatList',
  SET_REPORT_FORMAT_DISPLAY_PARAMS = 'report/set/reportFormatDisplayParams',
  SET_SELECTED_REPORT_FORMAT = 'report/set/selectedReportFormat',
  SET_SELECT_REPORT_ITEM_LIST = 'report/set/selectReportItemList',
}

export type ReportAction =
  | Action<ReportActionTypes.INIT>
  | Action<ReportActionTypes.REFRESH>
  | PayloadAction<boolean, ReportActionTypes.LOADING>
  | PayloadAction<boolean, ReportActionTypes.REPORT_FORMAT_LIST_LOADING>
  | PayloadAction<boolean, ReportActionTypes.SUBMITTING>
  | PayloadAction<Property[], ReportActionTypes.SET_PROPERTY_LIST>
  | PayloadAction<DisplayParams, ReportActionTypes.SET_PROPERTY_LIST_DISPLAY_PARAMS>
  | PayloadAction<Contractor[], ReportActionTypes.SET_CONTRACTOR_LIST>
  | PayloadAction<DisplayParams, ReportActionTypes.SET_CONTRACTOR_LIST_DISPLAY_PARAMS>
  | PayloadAction<string, ReportActionTypes.SET_REPORT_TARGET_NAME>
  | PayloadAction<ReportItem[], ReportActionTypes.SET_REPORT_ITEM_LIST>
  | PayloadAction<ReportItem[], ReportActionTypes.SET_REPORT_ITEM_LIST_ALL>
  | PayloadAction<DisplayParams, ReportActionTypes.SET_REPORT_ITEM_DISPLAY_PARAMS>
  | PayloadAction<ReportFormat[], ReportActionTypes.SET_REPORT_FORMAT_LIST>
  | PayloadAction<DisplayParams, ReportActionTypes.SET_REPORT_FORMAT_DISPLAY_PARAMS>
  | PayloadAction<ReportFormat | null, ReportActionTypes.SET_SELECTED_REPORT_FORMAT>
  | PayloadAction<SelectReportItem[], ReportActionTypes.SET_SELECT_REPORT_ITEM_LIST>;

/** @summary 建物の型 */
export type Property = {
  /** 建物ID */
  property_id: number;
  /** 建物名 */
  property_name: string;
  /** 住所 */
  address: string;
  /** オーナー */
  owner: string;
  /** 電話番号 */
  contact: string;
  /** URL */
  url: string;
};

/** @summary 建物リスト取得APIへのリクエストパラメータ */
export type APIGetPropertyListRequestParams = ListRequestParams & {
  is_managed_only?: boolean;
};

/** @summary 建物リストのAPIレスポンス */
export type APIGetPropertyListResponse = {
  result: {
    property_list: Property[];
    display_params: DisplayParams;
  };
};

/** @summary 契約先の型 */
export type Contractor = {
  /** 企業ID */
  company_id: number;
  /** 企業名 */
  company_name: string;
  /** 住所 */
  address: string;
  /** 代表者氏名 */
  representative_name: string;
  /** 電話番号 */
  tel: string;
  /** URL */
  url: string;
};

/** @summary 契約先リスト取得APIへのリクエストパラメータ */
export type APIGetContractorListRequestParams = ListRequestParams & {
  name?: string;
};

/** @summary 契約先リストのAPIレスポンス */
export type APIGetContractorListResponse = {
  result: {
    affiliate_company_list: Contractor[];
    display_params: DisplayParams;
  };
};

/**
 * @summary 月報出力対象(定期業務、工事、案件)の型
 */
export type ReportItem = {
  /** 建物ID */
  property_id: number;
  /** 建物名 */
  property_name: string;
  /** 項目名 */
  item_name: string;
  /** 契約先会社ID */
  affiliate_company_id: number;
  /** 契約先会社名 */
  affiliate_company_name: string;
  /** 業務種別ID */
  item_type_id: number;
  /** 業務種別名 */
  item_type_name: string;
  /** 業務ID */
  item_id: number;
  /** ステータスID */
  status_id: number;
  /** ステータス名 */
  status_name: string;
  /** 開始日 */
  start_date: string;
  /** 終了日 */
  end_date: string;
  /** 承認日 */
  approval_date: string;
  /** 承認者ID */
  approval_user_id: number;
  /** 承認者名 */
  approval_user_name: string;
  /** 周期のID */
  repeat_type_id: number;
  /** 周期の名前 */
  repeat_type_name: string;
  /** 周期のカスタム設定 */
  custom_setting: string;
  /** 選択済みフラグ */
  is_selected: boolean;
};

/**
 * @summary 選択された月報出力対象の型
 */
export type SelectReportItem = {
  /** 業務種別ID */
  item_type_id: number;
  /** 業務ID */
  item_id: number;
};

/** @summary 月報の出力対象リスト取得APIに指定できるリクエストパラメータ */
export type APIGetReportItemListRequestParams = ListRequestParams & {
  /** 定期業務、工事、案件名 */
  item_name?: string;
  /** 建物名 */
  property_name?: string;
  /** 契約先 */
  affiliate_company_name?: string;
  /** 業務種別 */
  item_type_name?: string;
  /** 対応状況 */
  status_name?: string;
  /** 計画名 */
  name?: string;
};

/** @summary 月報の出力対象リストのAPIレスポンス */
export type APIGetReportItemListResponse = {
  result: {
    report_items: ReportItem[];
    display_params: DisplayParams;
  };
};

/** @summary 月報フォーマットの型 */
export type ReportFormat = {
  /** フォーマットID */
  format_id: number;
  /** ファイルID */
  file_id: string;
  /** ファイル名 */
  file_name: string;
  /** 登録日次 */
  registered_datetime: string;
  /** 更新者ID */
  registered_user_id: number;
  /** 更新者名 */
  registered_user_name: string;
};

/** @summary 月報フォーマットリスト取得APIに指定できるリクエストパラメータ */
export type APIGetReportFormatListRequestParams = ListRequestParams & {
  /** ファイル名 */
  file_name?: string;
};

/** @summary 月報フォーマットリストのAPIレスポンス */
export type APIGetReportFormatListResponse = {
  result: {
    report_formats: ReportFormat[];
    display_params: DisplayParams;
  };
};

/** @summary 月報取得APIに指定できるリクエストのクエリパラメータ */
export type APIDownloadReportRequestQueryParams = {
  /** フォーマットID */
  format_file_id: number;
};

/** @summary 月報取得APIに指定できるリクエストのボディパラメータ */
export type APIDownloadReportRequestBodyParams = {
  /** 月報データを特定する業務種別とIDの組 */
  target_records: {
    /** 業務種別ID */
    item_type_id: number;
    /** 業務ID */
    item_id: number;
  }[];
  /** 出力対象シート */
  target_sheets: number[];
};
